import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  RefreshCw, Package, Truck, Bike, AlertTriangle, BarChart3,
  CheckCircle2, PackageX, ArrowRight, Inbox, CircleAlert,
} from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, Tooltip as RTooltip } from 'recharts';
import { api } from '../utils/api';
import Spinner from '../components/Spinner';
import { Button, Card, PageHeader, StatCard, Alert, EmptyState } from '../components/ui';

function formatUsd(amount) {
  if (!amount) return '$0';
  return '$' + Number(amount).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export default function Dashboard() {
  const [orders, setOrders]       = useState([]);
  const [counts, setCounts]       = useState({});
  const [stats, setStats]         = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [syncing, setSyncing]     = useState(false);
  const [error, setError]         = useState(null);

  // 30 günlük ciro serisi. Analiz ucu ağır olduğu için ANA yüklemeyi bekletmez —
  // ayrı ve sonradan gelir; gelene kadar grafik yerinde iskelet durur.
  const [trend, setTrend] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  async function loadData() {
    try {
      const [ord, sync, st, cnts] = await Promise.all([
        api.getOrders({ status: 'awaiting_packaging' }),
        api.getSyncStatus(),
        api.getOrderStats(),
        api.getOrderCounts(),
      ]);
      setOrders(ord);
      setSyncStatus(sync);
      setStats(st);
      setCounts(cnts);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);
  useEffect(() => {
    api.getAnalytics(30)
      // ⚠️ Uç revenue'yu METİN döndürüyor ("987.15"); recharts sayı ister, metinle
      // grafik sessizce boş çizilir. Sayıya burada çevriliyor.
      .then((d) => setTrend((Array.isArray(d?.trend) ? d.trend : []).map((x) => ({
        ...x, revenue: Number(x.revenue) || 0, order_count: Number(x.order_count) || 0,
      }))))
      .catch(() => setTrend([]));   // grafik olmazsa panel yine çalışır
  }, []);

  async function handleSync() {
    setSyncing(true);
    try {
      await api.syncOrders();
      await loadData();
    } catch (e) {
      setError(e.message);
    } finally {
      setSyncing(false);
    }
  }

  // Gecikmiş = shipment_date geçmiş, henüz paketlenmemiş
  const overdue = orders.filter(
    (o) => o.shipment_date && o.shipment_date.slice(0, 10) < today
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>;
  }

  return (
    <div className="p-6 lg:p-8 max-w-[1600px]">
      {/* ── Hero ───────────────────────────────────────────────────────────
          Ayın cirosu ve 30 günlük eğrisi tek blokta. Amaç: panele girer girmez
          "işler nasıl gidiyor" sorusunun cevabı görünsün; eskiden bu rakam sekiz
          eş görünümlü kartın içinde kayboluyordu. */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-700 via-brand-600 to-indigo-600 text-white mb-5 shadow-pop">
        <div className="absolute -right-16 -top-24 w-80 h-80 rounded-full bg-white/10 blur-2xl pointer-events-none" />
        <div className="absolute -left-10 -bottom-24 w-72 h-72 rounded-full bg-indigo-400/20 blur-2xl pointer-events-none" />

        <div className="relative flex flex-col lg:flex-row lg:items-stretch gap-6 p-6 lg:p-7">
          <div className="lg:w-[36%] flex flex-col justify-between">
            <div>
              <div className="text-2xs font-semibold tracking-widest text-white/60 uppercase">
                {new Date().toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
              <h1 className="text-xl font-semibold tracking-tight mt-1">Dashboard</h1>
              <div className="mt-5">
                <div className="text-2xs font-medium tracking-wider text-white/60 uppercase">Bu ay ciro</div>
                <div className="text-4xl lg:text-5xl font-semibold tracking-tight tabular-nums mt-1">
                  {stats ? formatUsd(stats.month_revenue) : '—'}
                </div>
                <div className="text-xs text-white/70 mt-1.5 tabular-nums">
                  {stats?.month_orders ?? '—'} sipariş · {stats?.delivered_month ?? '—'} teslim
                </div>
              </div>
            </div>
            <div className="mt-6">
              <button onClick={handleSync} disabled={syncing}
                className="inline-flex items-center gap-2 rounded-xl bg-white/15 hover:bg-white/25 ring-1 ring-inset ring-white/25
                           px-4 py-2 text-sm font-medium backdrop-blur transition-colors disabled:opacity-60">
                <RefreshCw size={15} className={syncing ? 'animate-spin' : ''} />
                {syncing ? 'Sync ediliyor…' : 'Şimdi Sync Et'}
              </button>
            </div>
          </div>

          {/* 30 günlük eğri */}
          <div className="lg:flex-1 min-w-0">
            <div className="text-2xs font-medium tracking-wider text-white/60 uppercase mb-1">Son 30 gün</div>
            <div className="h-36 lg:h-full lg:min-h-[9rem]">
              {trend === null ? (
                <div className="h-full w-full rounded-xl bg-white/10 animate-pulse" />
              ) : trend.length === 0 ? (
                <div className="h-full flex items-center text-xs text-white/60">Trend verisi yok.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend} margin={{ top: 6, right: 0, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="heroFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#fff" stopOpacity={0.45} />
                        <stop offset="100%" stopColor="#fff" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <RTooltip
                      cursor={{ stroke: 'rgba(255,255,255,.45)' }}
                      contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 10,
                                      fontSize: 12, color: '#fff', padding: '6px 10px' }}
                      labelStyle={{ color: '#94a3b8', fontSize: 11 }}
                      labelFormatter={(_, p) => p?.[0]?.payload?.label || ''}
                      formatter={(v) => [typeof v === 'number' ? v.toLocaleString('tr-TR') : v, 'Ciro']}
                    />
                    <Area type="monotone" dataKey="revenue"
                      stroke="#fff" strokeWidth={2} fill="url(#heroFill)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && <Alert ton="error" icon={CircleAlert} className="mb-5">{error}</Alert>}

      {/* Operasyon — anlık durum */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <StatCard icon={Package} label="Bekleyen" deger={counts.awaiting_packaging ?? '—'}
          alt="Paketleme bekliyor" ton="amber" />
        <StatCard icon={Truck} label="Teslim Bekliyor" deger={counts.awaiting_deliver ?? '—'}
          alt="Kurye alımı bekleniyor" ton="brand" />
        <StatCard icon={Bike} label="Kargoda" deger={counts.delivering ?? '—'}
          alt="Dağıtımda" ton="indigo" />
        {/* Gecikmiş: sıfırken yeşil değil nötr durur — "0 gecikme" bir başarı
            rozeti değil, normal hâldir; renk yalnız sorun varken dikkat çeksin. */}
        <StatCard icon={AlertTriangle} label="Gecikmiş" deger={overdue.length}
          alt="Sevk tarihi geçmiş" ton={overdue.length > 0 ? 'rose' : 'slate'} />
      </div>

      {/* Ay sonu — birikimli */}
      {/* Ciro hero'da; burada tekrarlanmıyor */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <StatCard icon={BarChart3} label="Bu Ay Sipariş" deger={stats?.month_orders ?? '—'}
          alt="İptal edilmeyenler" ton="brand" />
        <StatCard icon={CheckCircle2} label="Bu Ay Teslim" deger={stats?.delivered_month ?? '—'}
          alt="Başarılı teslim" ton="emerald" />
        <StatCard icon={PackageX} label="Stokta Yok" deger={stats?.out_of_stock ?? '—'}
          alt="Sıfır stoklu ürün" ton={stats?.out_of_stock > 0 ? 'rose' : 'slate'} />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Bekleyen siparişler */}
        <Card
          className="lg:col-span-2"
          padding={false}
          baslik="Bekleyen Siparişler"
          altBaslik={orders.length ? `${orders.length} sipariş paketleme bekliyor` : null}
          aksiyon={
            <Link to="/orders"
              className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700">
              Tümünü gör <ArrowRight size={13} />
            </Link>
          }
        >
          <div className="divide-y divide-slate-100">
            {orders.slice(0, 8).map((order) => {
              const items = (order.items || []).filter(Boolean);
              const isLate = order.shipment_date && order.shipment_date.slice(0, 10) < today;
              return (
                <Link
                  key={order.posting_number}
                  to={`/orders/${order.posting_number}`}
                  className="flex items-center justify-between gap-3 px-5 py-2.5 hover:bg-slate-50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-brand-700 font-mono">{order.posting_number}</div>
                    <div className="text-xs text-slate-400 mt-0.5 truncate">
                      {items.map((i) => i.offer_id).join(', ').slice(0, 60) || '—'}
                    </div>
                  </div>
                  <div className={`text-xs font-medium flex-shrink-0 inline-flex items-center gap-1 tabular-nums
                                   ${isLate ? 'text-rose-600' : 'text-slate-500'}`}>
                    {isLate && <AlertTriangle size={12} />}
                    {order.shipment_date
                      ? new Date(order.shipment_date).toLocaleDateString('tr-TR')
                      : '—'}
                  </div>
                </Link>
              );
            })}
            {!orders.length && (
              <EmptyState icon={Inbox} baslik="Bekleyen sipariş yok"
                aciklama="Paketleme bekleyen sipariş olmadığında burası boş kalır." />
            )}
          </div>
        </Card>

        {/* Sync durumu */}
        <Card baslik="Sync Durumu" altBaslik="Ozon'dan sipariş çekme">
          {syncStatus?.lastSync ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span className="text-xs font-medium text-emerald-600">Çalışıyor</span>
              </div>
              <div>
                <div className="text-xs text-slate-400">Son başarılı sync</div>
                <div className="text-sm font-medium text-slate-800 mt-0.5 tabular-nums">
                  {new Date(syncStatus.lastSync.timestamp).toLocaleString('tr-TR')}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Güncellenen sipariş</div>
                <div className="text-sm font-medium text-slate-800 mt-0.5 tabular-nums">
                  {syncStatus.lastSync.synced}
                </div>
              </div>
            </div>
          ) : syncStatus?.lastSyncError ? (
            <Alert ton="error" icon={CircleAlert}>{syncStatus.lastSyncError}</Alert>
          ) : (
            <div className="text-sm text-slate-400">Henüz sync yapılmadı.</div>
          )}
        </Card>
      </div>
    </div>
  );
}
